import React, { useState, useEffect } from 'react';
import { BoothService } from 'app/services/BoothService';
import { useAuth } from 'app/context/auth-context';
import { Button, Modal } from 'react-bootstrap-v5';
import { AppModal } from 'app/modules/shared/modals/AppModal';
import { AlertModal } from 'app/modules/shared/modals/AlertModal';
import { Booth, CreateBoothInput } from 'app/API';
import { useAppContext } from 'app/context/app-context';
import CreateEditBooth from './CreateEditBooth';
import Select, { components } from 'react-select';

import { VJFPUserService } from 'app/services/VJFPUserService';
import { OrgService } from 'app/services/OrgService';
import { ONE_MIN_IN_MILLISECONDS, getStyleByIndex } from 'app/helpers/MiscUtils';
import { Trash, ChevronUp, ChevronDown } from 'react-bootstrap-icons';
import { useS3Object } from 'app/context/S3ObjectContext';
import { VJFPUser } from 'app/API';

// Storage configuration removed (handled globally)

type ManageBoothsProps = {
  onLoad: (isAnyBoothLoaded: boolean) => void;
  orgOnBoardingProgressPercentage?: number;
  orgName: string;
  orgJobBoardUrl?: string;
  orgLogo?: string;
};

const UnassignedRecruiters: React.FC<{
  recruiterData: Map<number, VJFPUser>;
  unassignedRecruiters: number[];
  isCollapsed: boolean;
  onToggle: () => void;
  s3ObjectCache: Record<string, string>;
}> = ({ recruiterData, unassignedRecruiters, isCollapsed, onToggle, s3ObjectCache }) => (
  <div className="bg-white shadow-sm border rounded p-4 w-100 mb-7">
    <div className='d-flex align-items-center justify-content-between cursor-pointer' onClick={onToggle}>
      <div className='d-flex align-items-center'>
        <i className='bi bi-people-fill fs-3 text-primary me-2'></i>
        <h6 className='fw-bold mb-0'>Unassigned Recruiters ({unassignedRecruiters.length})</h6>
      </div>
      <button className="btn btn-icon btn-sm btn-light-primary">
        {isCollapsed ? <ChevronDown /> : <ChevronUp />}
      </button>
    </div>

    {!isCollapsed && (
      <div className="mt-4">
        {unassignedRecruiters.length > 0 ? (
          <div className="d-flex flex-row flex-wrap gap-3">
            {unassignedRecruiters.map((recruiterId, index) => {
              const recruiter = recruiterData.get(recruiterId);
              const initials = recruiter ? `${recruiter.firstName?.charAt(0)}${recruiter.lastName?.charAt(0)}` : '';
              const profilePic = recruiter?.avatar ? s3ObjectCache[recruiter.avatar] : null;

              return (
                <div key={recruiterId} className="d-flex align-items-center p-2 bg-light-primary rounded border border-dashed border-primary gap-2">
                  <div className="symbol symbol-25px">
                    {profilePic ? (
                      <img src={profilePic} alt={initials} crossOrigin="anonymous" style={{ objectFit: 'cover' }} />
                    ) : (
                      <div className={"symbol-label fs-8 fw-bold bg-" + getStyleByIndex(index) + " text-inverse-" + getStyleByIndex(index)}>
                        {initials}
                      </div>
                    )}
                  </div>
                  <span className='fw-semibold text-gray-800 fs-7'>{recruiter ? `${recruiter.firstName} ${recruiter.lastName}` : ''}</span>
                </div>
              );
            })}
          </div>
        ) : (
          <div className='text-center py-2'>
            <span className='text-gray-400 fw-semibold fs-8'>All recruiters assigned</span>
          </div>
        )}
      </div>
    )}
  </div>
);

interface BoothCardProps {
  booth: Booth;
  recruiterData: Map<number, VJFPUser>;
  index: number;
  assignRecruiter: (recruiterId: number, boothIndex: number | 'unassigned') => void;
  unAssignedRecruiters: number[];
  handleEdit: (booth: Booth) => void;
  handleDelete: (booth: Booth) => void;
  showDeleteIcon: boolean;
  companyImageUrl?: string;
  s3ObjectCache: Record<string, string>;
}

const BoothCard: React.FC<BoothCardProps> = ({
  booth,
  recruiterData,
  index,
  assignRecruiter,
  unAssignedRecruiters,
  handleEdit,
  handleDelete,
  showDeleteIcon,
  companyImageUrl,
  s3ObjectCache
}) => {
  const [isRecruitersExpanded, setIsRecruitersExpanded] = useState(true);

  return (
    <div className="card border border-gray-300 shadow-sm h-100 w-100 w-md-350px">
      <div className="card-header border-0 pt-7 min-h-150px d-flex align-items-center justify-content-center bg-light position-relative">
        {showDeleteIcon && (
          <button
            onClick={() => handleDelete(booth)}
            className="btn btn-icon btn-sm btn-light-danger position-absolute top-0 end-0 m-2"
            title="Delete Booth"
          >
            <i className="bi bi-trash fs-4"></i>
          </button>
        )}
        <div className="symbol symbol-100px symbol-lg-150px">
          <img
            src={companyImageUrl}
            alt={booth.name}
            style={{ objectFit: 'contain' }}
            crossOrigin="anonymous"
          />
        </div>
      </div>

      <div className="card-body p-7 d-flex flex-column">
        <div className="mb-5">
          <div className="fs-3 fw-bold text-dark text-hover-primary mb-1">{booth.name}</div>
          <p className="text-gray-500 fw-semibold fs-7" style={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            minHeight: '2.5rem'
          }}>
            {booth.description || 'No description provided.'}
          </p>
        </div>

        <div className="separator separator-dashed mb-5"></div>

        <div className="d-flex flex-stack mb-3">
          <div className="text-gray-700 fw-semibold fs-7">Interview duration:</div>
          <div className="badge badge-light-info fw-bold">{(booth.chatDuration || 0) / ONE_MIN_IN_MILLISECONDS} mins</div>
        </div>

        <div className="d-flex flex-stack mb-5">
          <div className="text-gray-700 fw-semibold fs-7">Minimum Score:</div>
          <div className="badge badge-light-success fw-bold">{booth.minimumScore}</div>
        </div>

        <div className="mb-3">
          <div className="d-flex flex-stack mb-2 cursor-pointer" onClick={() => setIsRecruitersExpanded(!isRecruitersExpanded)}>
            <label className="fs-7 fw-bold text-gray-800 mb-0">Recruiters</label>
            <button className="btn btn-icon btn-sm btn-light-primary">
              {isRecruitersExpanded ? <ChevronUp /> : <ChevronDown />}
            </button>
          </div>

          {isRecruitersExpanded && (
            <>
              <div className="bg-light rounded p-4 mb-3" style={{ minHeight: '80px' }}>
                {booth.recruiters && booth.recruiters.length > 0 ? (
                  <div className="d-flex flex-column gap-2">
                    {booth.recruiters.map((recruiterId, idx) => {
                      const recruiter = recruiterData.get(Number(recruiterId!));
                      const initials = recruiter ? `${recruiter.firstName?.charAt(0)}${recruiter.lastName?.charAt(0)}` : '';
                      const profilePic = recruiter?.avatar ? s3ObjectCache[recruiter.avatar] : null;

                      return (
                        <div key={recruiterId} className="d-flex justify-content-between align-items-center bg-white p-2 rounded border border-gray-200">
                          <div className="d-flex align-items-center gap-2 overflow-hidden">
                            <div className="symbol symbol-30px flex-shrink-0">
                              {profilePic ? (
                                <img src={profilePic} alt={initials} crossOrigin="anonymous" style={{ objectFit: 'cover' }} />
                              ) : (
                                <div className={"symbol-label fs-7 fw-bold bg-" + getStyleByIndex((Number(recruiterId) || 0) % 7) + " text-inverse-" + getStyleByIndex((Number(recruiterId) || 0) % 7)}>
                                  {initials}
                                </div>
                              )}
                            </div>
                            <span className="fs-7 fw-semibold text-gray-700 text-truncate">
                              {recruiter ? `${recruiter.firstName} ${recruiter.lastName}` : 'Unknown Recruiter'}
                            </span>
                          </div>
                          <button
                            onClick={() => assignRecruiter(recruiterId!, 'unassigned')}
                            className="btn btn-icon btn-sm btn-light-danger btn-active-danger ms-2 flex-shrink-0"
                            title="Remove Recruiter"
                          >
                            <i className="bi bi-person-dash fs-4"></i>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="d-flex flex-center h-60px border border-dashed border-gray-300 rounded">
                    <span className="text-gray-400 fs-8 fw-semibold">No recruiters assigned</span>
                  </div>
                )}
              </div>

              <Select
                className="react-select-container"
                classNamePrefix="react-select"
                options={unAssignedRecruiters.map(recruiterId => {
                  const recruiter = recruiterData.get(recruiterId);
                  return {
                    value: recruiterId,
                    label: recruiter ? `${recruiter.firstName} ${recruiter.lastName}` : 'Unknown',
                    recruiter
                  };
                })}
                placeholder="+ Assign Recruiter"
                isSearchable
                value={null}
                onChange={(option: any) => {
                  if (option?.value) assignRecruiter(option.value, index);
                }}
                formatOptionLabel={(option: any) => {
                  const recruiter = option.recruiter;
                  const initials = recruiter ? `${recruiter.firstName?.charAt(0)}${recruiter.lastName?.charAt(0)}` : '';
                  const profilePic = recruiter?.avatar ? s3ObjectCache[recruiter.avatar] : null;
                  return (
                    <div className="d-flex align-items-center gap-2">
                      <div className="symbol symbol-25px">
                        {profilePic ? (
                          <img src={profilePic} alt={initials} crossOrigin="anonymous" style={{ objectFit: 'cover' }} />
                        ) : (
                          <div className={"symbol-label fs-8 fw-bold bg-" + getStyleByIndex((option.value ?? 0) % 7) + " text-inverse-" + getStyleByIndex((option.value ?? 0) % 7)}>
                            {initials}
                          </div>
                        )}
                      </div>
                      <span className="fs-7 fw-semibold text-gray-700">{option.label}</span>
                    </div>
                  );
                }}
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: '35px',
                    height: '35px',
                    backgroundColor: '#f5f8fa',
                    border: 'none',
                    borderRadius: '0.475rem',
                  }),
                  valueContainer: (base) => ({
                    ...base,
                    padding: '0 8px',
                  }),
                  indicatorsContainer: (base) => ({
                    ...base,
                    height: '35px',
                  }),
                  menu: (base) => ({
                    ...base,
                    zIndex: 9999
                  })
                }}
              />
            </>
          )}
        </div>

        <div className="mt-auto pt-5 d-flex gap-2">
          <Button variant="primary" className="btn-sm flex-grow-1" onClick={() => handleEdit(booth)}>
            Manage Booth
          </Button>
        </div>
      </div>
    </div>
  );
};

const ManageBooths: React.FC<ManageBoothsProps> = (props: ManageBoothsProps) => {
  const [booths, setBooths] = useState<Booth[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [boothToDelete, setBoothToDelete] = useState<Booth | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedBooth, setSelectedBooth] = useState<Booth | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [recruitersData, setRecruitersData] = useState(new Map<number, VJFPUser>());
  const [unAssignedRecruiters, setUnAssignedRecruiters] = useState<number[]>([]);
  const [isUnassignedCollapsed, setIsUnassignedCollapsed] = useState(false);
  const { getS3ObjectUrl, s3ObjectCache } = useS3Object();
  const { user } = useAuth();
  const { jobFair } = useAppContext();

  const boothService = new BoothService();

  const fetchBooths = async (isLoadUsers: boolean = false) => {
    setIsLoading(true);
    if (!jobFair?.id) return;
    const boothList = await boothService.getAllBooths(jobFair.id);
    props.onLoad(boothList.length > 0);

    boothList.forEach(booth => {
      if (booth.companyImageUrl) {
        getS3ObjectUrl(booth.companyImageUrl);
      }
    });

    if (props.orgLogo) {
      getS3ObjectUrl(props.orgLogo);
    }

    const sortedBooths = boothList.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

    if (isLoadUsers && boothList?.length) {
      if (user?.id) {
        const vjfpUserService = new VJFPUserService();
        const fetchedUsers = await vjfpUserService.getUsers(user.organizationId);
        const map = new Map<number, VJFPUser>();
        fetchedUsers.forEach((user) => {
          map.set(Number(user.id), user);
          if (user.avatar) {
            getS3ObjectUrl(user.avatar);
          }
        });
        setRecruitersData(map);

        const assignedRecruiters = new Set<number>(
          boothList
            .flatMap((x) => x.recruiters || [])
            .filter((id) => id !== null && id !== undefined)
            .map((id) => Number(id))
        );

        const recruiterIds = Array.from(map.keys());
        const unassignedRecruiterIds = recruiterIds.filter((id) => !assignedRecruiters.has(id));
        setUnAssignedRecruiters(unassignedRecruiterIds);
      }
    }
    setBooths(sortedBooths);
    setIsLoading(false);
  };

  useEffect(() => {
    fetchBooths(true);
  }, [jobFair?.id]);

  const handleCreateBooth = async (values: CreateBoothInput) => {
    if (!user?.id || !jobFair?.id) return;
    values.organizationId = user.organizationId;
    let createdBooth: Booth | undefined;
    try {
      if (selectedBooth?.id) {
        await boothService.updateBooth(selectedBooth.jobFairId, selectedBooth.id, values);
      } else {
        if (!values.recruiters || values.recruiters.length === 0) {
          const isAlreadyAssigned = booths.some(b => b.recruiters?.includes(user.id));
          if (!isAlreadyAssigned) {
            values.recruiters = [user.id];
          }
        }
        createdBooth = await boothService.createBooth(jobFair?.id, values);
      }

      if (values.recruiters?.length && props.orgOnBoardingProgressPercentage && props.orgOnBoardingProgressPercentage < 100) {
        new OrgService().updateOrg(user.organizationId, {
          onBoardingProgress: {
            progress: 100,
            lastCompletedStep: 'AssignedARecruiter'
          }
        });
      }
      else if (props.orgOnBoardingProgressPercentage && props.orgOnBoardingProgressPercentage < 80) {
        new OrgService().updateOrg(user.organizationId, {
          onBoardingProgress: {
            progress: 80,
            lastCompletedStep: 'BoothConfigured'
          }
        });
      }

      setShowModal(false);
      setSelectedBooth(undefined);
      await fetchBooths();
    } catch (error: any) {
      setErrorMessage(error?.message || 'Failed to save booth. Please try again.');
      setShowErrorModal(true);
    }
  };

  const handleDeleteBooth = async (booth: Booth) => {
    setBoothToDelete(booth);
    setShowDeleteAlert(true);
  };

  const handleEditBooth = async (booth: Booth) => {
    setSelectedBooth(booth);
    setShowModal(true);
  };

  const confirmDeleteBooth = async () => {
    if (boothToDelete) {
      await boothService.deleteBooth(boothToDelete.jobFairId, boothToDelete.id);
    }
    setShowDeleteAlert(false);
    setBoothToDelete(null);
    await fetchBooths(true);
  };

  const assignRecruiter = async (recruiterId: number, targetBoothIndex: number | 'unassigned') => {
    setIsLoading(true); // Show loading indicator

    const _booths = JSON.parse(JSON.stringify(booths));
    const _unassigned = [...unAssignedRecruiters];
    const affectedBoothIndexes: number[] = [];

    if (targetBoothIndex === 'unassigned') {
      // Remove recruiter from all booths and add to unassigned
      _booths.forEach((booth: Booth, index: number) => {
        if (booth.recruiters?.includes(recruiterId)) {
          booth.recruiters = booth.recruiters.filter((id) => id !== recruiterId);
          affectedBoothIndexes.push(index);
        }
      });
      _unassigned.push(recruiterId);
    } else {
      // Remove recruiter from unassigned
      const recruiterIndex = _unassigned.indexOf(recruiterId);
      if (recruiterIndex > -1) _unassigned.splice(recruiterIndex, 1);

      // Remove recruiter from any other booth
      _booths.forEach((booth: Booth, index: number) => {
        if (booth.recruiters?.includes(recruiterId)) {
          booth.recruiters = booth.recruiters.filter((id) => id !== recruiterId);
          affectedBoothIndexes.push(index);
        }
      });

      // Assign recruiter to the target booth
      _booths[targetBoothIndex].recruiters = [...(_booths[targetBoothIndex].recruiters || []), recruiterId];
      if (!affectedBoothIndexes.includes(targetBoothIndex)) {
        affectedBoothIndexes.push(targetBoothIndex);
      }
    }

    setBooths(_booths);
    setUnAssignedRecruiters(_unassigned);

    try {
      // Update ALL affected booths (both the booth that lost the recruiter and the one that gained it)
      await Promise.all(affectedBoothIndexes.map(async (index) => {
        const booth = _booths[index];
        await boothService.updateBooth(booth.jobFairId, booth.id, {
          recruiters: (booth.recruiters || []).map((recruiter: number | null) => recruiter || null),
        });
      }));
      if (props.orgOnBoardingProgressPercentage && props.orgOnBoardingProgressPercentage < 100) {
        new OrgService().updateOrg(user!.organizationId, {
          onBoardingProgress: {
            progress: 100,
            lastCompletedStep: 'AssignedARecruiter'
          }
        });
      }
    } catch (error) {
      setErrorMessage('Failed to update recruiter assignment. Please try again.');
      setShowErrorModal(true);
    } finally {
      setIsLoading(false); // Hide loading indicator
    }
  };

  return (
    <div className="card">
      <div className="card-header border-0 pt-6">
        <h3 className="card-title align-items-start flex-column">
          <span className="card-label fw-bold fs-3 mb-1">
            {booths.length > 0 ? 'Manage Booths' : 'Booth Registration'}
          </span>
        </h3>
        <div className="card-toolbar">
          {booths.length > 0 && (
            <button
              className="btn btn-lg btn-light-primary"
              onClick={() => {
                setShowModal(true);
                setSelectedBooth(undefined);
              }}
            >
              <i className="ki-duotone ki-plus fs-2"></i>Create New Booth
            </button>
          )}
        </div>
      </div>
      <div className="card-body py-4">
        {isLoading ? (
          <div className="d-flex justify-content-center py-10">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <div className="d-flex flex-column gap-7">
            <UnassignedRecruiters
              recruiterData={recruitersData}
              unassignedRecruiters={unAssignedRecruiters}
              isCollapsed={isUnassignedCollapsed}
              onToggle={() => setIsUnassignedCollapsed(!isUnassignedCollapsed)}
              s3ObjectCache={s3ObjectCache}
            />
            <div className="d-flex flex-wrap gap-7 align-items-stretch">
              {booths && booths.length > 0 ? (
                booths.map((booth: Booth, index: number) => (
                  <BoothCard
                    key={booth.id}
                    booth={booth}
                    recruiterData={recruitersData}
                    index={index}
                    assignRecruiter={assignRecruiter}
                    unAssignedRecruiters={unAssignedRecruiters}
                    handleEdit={handleEditBooth}
                    handleDelete={handleDeleteBooth}
                    showDeleteIcon={booths.length > 1}
                    companyImageUrl={booth.companyImageUrl ? s3ObjectCache[booth.companyImageUrl] : (props.orgLogo ? s3ObjectCache[props.orgLogo] : undefined)}
                    s3ObjectCache={s3ObjectCache}
                  />
                ))
              ) : (
                <div className="w-100">
                  <div className="max-w-600px mx-auto">
                    <CreateEditBooth orgName={props.orgName} orgJobBoardUrl={props.orgJobBoardUrl} onSubmit={handleCreateBooth} isUserAlreadyAssigned={user?.id ? booths.some(b => b.recruiters?.includes(user.id)) : false} />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <Modal size="lg" show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{selectedBooth ? 'Edit Booth' : 'Create New Booth'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <CreateEditBooth
            orgName={props.orgName}
            onSubmit={handleCreateBooth}
            booth={selectedBooth}
            companyImageUrl={selectedBooth?.companyImageUrl ? s3ObjectCache[selectedBooth.companyImageUrl] : (props.orgLogo ? s3ObjectCache[props.orgLogo] : undefined)}
            orgJobBoardUrl={props.orgJobBoardUrl}
            isUserAlreadyAssigned={user?.id ? booths.some(b => b.recruiters?.includes(user.id)) : false}
          />
        </Modal.Body>
      </Modal>

      <AppModal
        show={showDeleteAlert}
        onSuccess={confirmDeleteBooth}
        onReject={() => setShowDeleteAlert(false)}
        onHide={() => setShowDeleteAlert(false)}
        title="Delete this Booth?"
      >
        Are you sure you want to delete this booth?
      </AppModal>

      <AlertModal show={showErrorModal} onOK={() => setShowErrorModal(false)} title="Error">
        {errorMessage}
      </AlertModal>
    </div>
  );
};

export default ManageBooths;
